/**
 * Wizard state machine.
 *
 * Owns: current step index, per-step status, shared form values, validation
 * errors, async-loading + api-error flags. Step components stay stateless and
 * read everything from this hook.
 *
 * Navigation rules:
 *   - `goNext()` validates the current step's fields (when it's a credentials
 *     step) before advancing. On failure, errors are surfaced via `showErrors`.
 *   - `goBack()` always works (no re-validation on the way back).
 *   - `goToStep(i)` is unguarded — callers (e.g. clicking a completed step in
 *     the indicator) are expected to handle their own gating.
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import { validateFields } from "./validators";
import type {
  ConnectorStepConfig,
  ConnectorStepStatus,
  ConnectorWizardConfig,
} from "./ConnectorWizardModal.types";

interface UseConnectorWizardArgs {
  config: ConnectorWizardConfig | null;
  /** Reset trigger — typically modal open vs closed (see `ConnectorWizardModal`). */
  resetKey?: string | null;
  /**
   * Stable connector identity from the modal prop. Avoid using `config.catalogKey`
   * here because `config` can transition from null -> loaded as async schema data
   * arrives, which would otherwise trigger an unintended full reset mid-flow.
   */
  wizardCatalogKey?: string | null;
  /** Optional key to persist in-progress wizard drafts across hard refresh. */
  persistKey?: string | null;
}

interface UseConnectorWizardResult {
  steps: ConnectorStepConfig[];
  currentStep: ConnectorStepConfig | null;
  currentStepIndex: number;
  stepStatuses: ConnectorStepStatus[];
  values: Record<string, unknown>;
  errors: Record<string, string>;
  showErrors: boolean;
  isLoading: boolean;
  apiError: string | null;
  /** Whether the current step has signaled it's ready to advance. Defaults to true. */
  canAdvance: boolean;
  setValue: (key: string, value: unknown) => void;
  setLoading: (loading: boolean) => void;
  setApiError: (message: string | null) => void;
  setCanAdvance: (canAdvance: boolean) => void;
  /** Mark current step completed and advance, returning false on validation block. */
  goNext: () => boolean;
  goBack: () => void;
  goToStep: (index: number) => void;
  /** Force-validate the current step's fields without advancing. */
  validateCurrentStep: () => boolean;
  /** Reset everything to step 0 with cleared values. Drives "Add another
   *  instance" on the Finish step without closing the modal. */
  restart: () => void;
  /** Clears persisted draft and resets wizard to initial state. */
  clearPersistedDraft: () => void;
}

const buildInitialValues = (
  config: ConnectorWizardConfig | null,
): Record<string, unknown> => {
  if (!config) return {};
  const out: Record<string, unknown> = {};
  for (const step of config.steps) {
    if (step.kind !== "credentials" || !step.fields) continue;
    for (const field of step.fields) {
      if (field.defaultValue !== undefined) out[field.key] = field.defaultValue;
    }
  }
  return out;
};

interface PersistedWizardDraft {
  currentStepIndex?: unknown;
  completedStepIds?: unknown;
  values?: unknown;
}

const readPersistedDraft = (
  persistKey: string | null | undefined,
): PersistedWizardDraft | null => {
  if (!persistKey) return null;
  try {
    const raw = window.sessionStorage.getItem(persistKey);
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return null;
    return parsed as PersistedWizardDraft;
  } catch {
    return null;
  }
};

const toDraftState = (draft: PersistedWizardDraft | null) => {
  const currentStepIndex =
    typeof draft?.currentStepIndex === "number"
      ? Math.max(0, Math.floor(draft.currentStepIndex))
      : 0;
  const completed = Array.isArray(draft?.completedStepIds)
    ? new Set(
        draft.completedStepIds.filter(
          (id): id is string => typeof id === "string",
        ),
      )
    : new Set<string>();
  const values =
    draft?.values && typeof draft.values === "object"
      ? (draft.values as Record<string, unknown>)
      : null;

  return { currentStepIndex, completed, values };
};

export const useConnectorWizard = ({
  config,
  resetKey,
  wizardCatalogKey,
  persistKey,
}: UseConnectorWizardArgs): UseConnectorWizardResult => {
  const steps = useMemo(() => config?.steps ?? [], [config]);
  const persistedDraft = useMemo(() => readPersistedDraft(persistKey), [persistKey]);
  const restoredDraftState = useMemo(() => toDraftState(persistedDraft), [persistedDraft]);

  const [currentStepIndex, setCurrentStepIndex] = useState(
    restoredDraftState.currentStepIndex,
  );
  const [completed, setCompleted] = useState<Set<string>>(
    restoredDraftState.completed,
  );
  const [values, setValues] = useState<Record<string, unknown>>(() =>
    restoredDraftState.values
      ? restoredDraftState.values
      : buildInitialValues(config),
  );
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [showErrors, setShowErrors] = useState(false);
  const [isLoading, setLoading] = useState(false);
  const [apiError, setApiErrorState] = useState<string | null>(null);
  const [canAdvance, setCanAdvanceState] = useState(true);

  /** Reset every piece of wizard state. Used both by the resetKey effect
   *  (modal opens with a new connector) and by `restart()` (the Finish
   *  step's "Add another instance" action). */
  const resetAll = useCallback(() => {
    const draftState = toDraftState(readPersistedDraft(persistKey));
    setCurrentStepIndex(draftState.currentStepIndex);
    setCompleted(draftState.completed);
    setValues(draftState.values ?? buildInitialValues(config));
    setErrors({});
    setShowErrors(false);
    setLoading(false);
    setApiErrorState(null);
    setCanAdvanceState(true);
  }, [config, persistKey]);

  const resetToInitial = useCallback(() => {
    setCurrentStepIndex(0);
    setCompleted(new Set<string>());
    setValues(buildInitialValues(config));
    setErrors({});
    setShowErrors(false);
    setLoading(false);
    setApiErrorState(null);
    setCanAdvanceState(true);
  }, [config]);

  const clearPersistedDraft = useCallback(() => {
    if (persistKey) {
      try {
        window.sessionStorage.removeItem(persistKey);
      } catch {
        // Ignore storage failures so wizard remains functional.
      }
    }
    resetToInitial();
  }, [persistKey, resetToInitial]);

  // Reset only on modal lifecycle / connector identity changes.
  const resetSignal = `${resetKey ?? ""}::${wizardCatalogKey ?? ""}`;
  useEffect(() => {
    resetAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resetSignal]);

  // Reset `canAdvance` to true on every step change. Steps that need gating
  // (e.g. TestStep) call setCanAdvance(false) in their own useEffect, which
  // runs after this — so the order is: step changes → canAdvance resets to
  // true → step mounts → step opts in to gating.
  useEffect(() => {
    setCanAdvanceState(true);
  }, [currentStepIndex]);

  useEffect(() => {
    if (steps.length === 0) return;
    setCurrentStepIndex((prev) => Math.min(prev, steps.length - 1));
  }, [steps.length]);

  // Auto-disable test/continue button when credentials step has validation errors
  useEffect(() => {
    const currentStep = steps[currentStepIndex];
    if (currentStep?.kind === "credentials" && showErrors) {
      const hasErrors = Object.keys(errors).length > 0;
      setCanAdvanceState(!hasErrors);
    }
  }, [currentStepIndex, steps, errors, showErrors]);

  useEffect(() => {
    if (!persistKey) return;
    const currentStep = steps[currentStepIndex];
    if (currentStep?.kind === "finish") {
      // Connection completed — remove the draft so re-opening the wizard
      // starts fresh instead of restoring the old finished-connection state.
      try {
        window.sessionStorage.removeItem(persistKey);
      } catch {
        // Ignore storage failures so wizard remains functional.
      }
      return;
    }
    try {
      window.sessionStorage.setItem(
        persistKey,
        JSON.stringify({
          currentStepIndex,
          completedStepIds: Array.from(completed),
          values,
        }),
      );
    } catch {
      // Ignore storage failures so wizard remains functional.
    }
  }, [persistKey, currentStepIndex, completed, values, steps]);

  const stepStatuses = useMemo<ConnectorStepStatus[]>(
    () =>
      steps.map((step, i) => {
        const currentStep = steps[currentStepIndex];
        const shouldMarkCredentialsError =
          currentStep?.kind === "test" &&
          i === currentStepIndex - 1 &&
          Boolean(apiError);
        if (shouldMarkCredentialsError) return "error";
        if (i === currentStepIndex) return "active";
        if (completed.has(step.id)) return "completed";
        return "pending";
      }),
    [steps, currentStepIndex, completed, apiError],
  );

  const setValue = useCallback((key: string, value: unknown) => {
    setValues((prev) => ({ ...prev, [key]: value }));
    // Clear the error for this field as the user types — feels more forgiving
    // than waiting for the next submit.
    setErrors((prev) => {
      if (!prev[key]) return prev;
      const { [key]: _, ...rest } = prev;
      return rest;
    });
  }, []);

  const setApiError = useCallback((message: string | null) => {
    setApiErrorState(message);
  }, []);

  const setCanAdvance = useCallback((next: boolean) => {
    setCanAdvanceState(next);
  }, []);

  const validateCurrentStep = useCallback((): boolean => {
    const step = steps[currentStepIndex];
    if (!step) return true;
    if (step.kind !== "credentials" || !step.fields) {
      setShowErrors(false);
      setErrors({});
      return true;
    }
    // OAuth-connected credentials step: the connection is already created
    // server-side (its id is in `values`) and the credential fields are
    // intentionally empty (the token lives server-side, never in the form).
    // Treat it as valid so goNext() advances and the footer CTA stays enabled.
    if (
      step.oauthProvider &&
      typeof values.connectionId === "string" &&
      values.connectionId.trim().length > 0
    ) {
      setShowErrors(false);
      setErrors({});
      return true;
    }
    const next = validateFields(step.fields, values);
    setErrors(next);
    setShowErrors(true);
    return Object.keys(next).length === 0;
  }, [steps, currentStepIndex, values]);

  const goNext = useCallback((): boolean => {
    const step = steps[currentStepIndex];
    if (!step) return false;
    const ok = validateCurrentStep();
    if (!ok) return false;
    setCompleted((prev) => {
      const next = new Set(prev);
      next.add(step.id);
      return next;
    });
    setShowErrors(false);
    setApiErrorState(null);
    setCurrentStepIndex((i) => Math.min(i + 1, steps.length - 1));
    return true;
  }, [steps, currentStepIndex, validateCurrentStep]);

  /** Drop "completed" status for every step at-or-after `fromIndex`.
   *  Backward navigation invalidates downstream progress (e.g. changing
   *  credentials makes the previously-fetched schemas stale), so the
   *  indicator should reflect that the user must re-traverse those steps. */
  const trimCompletedFrom = useCallback(
    (fromIndex: number) => {
      setCompleted((prev) => {
        const next = new Set(prev);
        for (let i = fromIndex; i < steps.length; i++) {
          next.delete(steps[i].id);
        }
        return next;
      });
    },
    [steps],
  );

  const goBack = useCallback(() => {
    setShowErrors(false);
    setApiErrorState(null);
    setCurrentStepIndex((i) => {
      const target = Math.max(i - 1, 0);
      // Trim from the new current step inclusive — leaving the step we're
      // about to land on no longer counts as "completed".
      trimCompletedFrom(target);
      return target;
    });
  }, [trimCompletedFrom]);

  const goToStep = useCallback(
    (index: number) => {
      if (index < 0 || index >= steps.length) return;
      setShowErrors(false);
      setApiErrorState(null);
      setCurrentStepIndex((current) => {
        // Forward jumps don't invalidate anything; only backward jumps do.
        if (index < current) trimCompletedFrom(index);
        return index;
      });
    },
    [steps.length, trimCompletedFrom],
  );

  return {
    steps,
    currentStep: steps[currentStepIndex] ?? null,
    currentStepIndex,
    stepStatuses,
    values,
    errors,
    showErrors,
    isLoading,
    apiError,
    canAdvance,
    setValue,
    setLoading,
    setApiError,
    setCanAdvance,
    goNext,
    goBack,
    goToStep,
    validateCurrentStep,
    restart: resetAll,
    clearPersistedDraft,
  };
};
